import React, { useEffect, useState, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { RefreshCw } from "lucide-react";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { workflowApi } from "@/services/api/workflowApi";
import type { DeviceBinding } from "@/components/shared/rendering/InteractionContext";

interface KasaDeviceInfo {
  deviceId: string;
  name: string;
  model: string;
  status: string;
  relayState: string;
  supportsBrightness: boolean;
  discoveredBy: string;
  host: string;
}

interface DeviceBindingPickerProps {
  currentBinding?: DeviceBinding;
  onSelect: (binding: DeviceBinding | undefined) => void;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const readBoolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

function readDevices(value: unknown): unknown[] {
  if (!isRecord(value)) return [];

  const dataDevices = isRecord(value.data) ? value.data.devices : undefined;
  if (Array.isArray(dataDevices)) return dataDevices;

  return Array.isArray(value.devices) ? value.devices : [];
}

function toKasaDeviceInfo(value: unknown): KasaDeviceInfo | null {
  if (!isRecord(value) || value.integration !== "kasa") return null;

  const deviceId = readString(value.deviceId);
  if (!deviceId) return null;

  const integrationMeta = isRecord(value.integrationMeta) ? value.integrationMeta : {};

  return {
    deviceId,
    name: readString(value.name) || readString(integrationMeta.alias) || deviceId,
    model: readString(integrationMeta.model) || "Unknown",
    status: readString(value.status) || "offline",
    relayState: readString(integrationMeta.relayState) || "off",
    supportsBrightness: readBoolean(integrationMeta.supportsBrightness) || false,
    discoveredBy: readString(value.discoveredBy) || "",
    host: readString(integrationMeta.host) || "",
  };
}

function toDeviceBinding(device: KasaDeviceInfo, orgSlug: string): DeviceBinding {
  return {
    type: "kasa",
    deviceId: device.deviceId,
    deviceName: device.name,
    controllerDeviceId: device.discoveredBy || "",
    orgSlug,
    capabilities: {
      toggle: true,
      brightness: device.supportsBrightness,
    },
  };
}

export const DeviceBindingPicker: React.FC<DeviceBindingPickerProps> = ({
  currentBinding,
  onSelect,
}) => {
  const organizations = useOrganizationStore((s) => s.organizations);
  const currentContext = useOrganizationStore((s) => s.currentContext);
  const fetchOrganizations = useOrganizationStore((s) => s.fetchOrganizations);

  const [selectedOrgSlug, setSelectedOrgSlug] = useState<string>("");
  const [devices, setDevices] = useState<KasaDeviceInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load organizations on mount
  useEffect(() => {
    if (organizations.length === 0) {
      fetchOrganizations().catch(() => undefined);
    }
  }, [organizations.length, fetchOrganizations]);

  // Auto-select org: use current context if in an org, otherwise first available
  useEffect(() => {
    if (selectedOrgSlug) return;
    if (currentContext?.type === "organization" && currentContext.orgSlug) {
      setSelectedOrgSlug(currentContext.orgSlug);
    } else if (organizations.length > 0) {
      setSelectedOrgSlug(organizations[0].slug);
    }
  }, [currentContext, organizations, selectedOrgSlug]);

  // Fetch devices when org changes
  const fetchDevices = useCallback(async (orgSlug: string) => {
    if (!orgSlug) return;
    setIsLoading(true);
    try {
      const result = await workflowApi.get<unknown>(`/api/orgs/${orgSlug}/devices`);
      const kasaDevices = readDevices(result)
        .map(toKasaDeviceInfo)
        .filter((device): device is KasaDeviceInfo => device !== null);
      setDevices(kasaDevices);
    } catch {
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedOrgSlug) {
      fetchDevices(selectedOrgSlug).catch(() => undefined);
    }
  }, [selectedOrgSlug, fetchDevices]);

  const online = devices.filter((d) => d.status === "online");
  const offline = devices.filter((d) => d.status !== "online");

  const handleDeviceSelect = (value: string) => {
    if (value === "__none__") {
      onSelect(undefined);
      return;
    }
    const device = devices.find((d) => d.deviceId === value);
    if (device) {
      onSelect(toDeviceBinding(device, selectedOrgSlug));
    }
  };

  return (
    <div className="space-y-3">
      {/* Org selector */}
      {organizations.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">Organization</Label>
          <Select value={selectedOrgSlug} onValueChange={setSelectedOrgSlug}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select organization..." />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((org) => (
                <SelectItem key={org.slug} value={org.slug}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Device selector */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Smart Device</Label>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0"
            onClick={() => {
              if (selectedOrgSlug) {
                fetchDevices(selectedOrgSlug).catch(() => undefined);
              }
            }}
            disabled={isLoading}
            title="Refresh devices"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <Select
          value={currentBinding?.deviceId || "__none__"}
          onValueChange={handleDeviceSelect}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Select a device..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">None (no device)</SelectItem>
            {online.length > 0 && (
              <SelectGroup>
                <SelectLabel>Online</SelectLabel>
                {online.map((d) => (
                  <SelectItem key={d.deviceId} value={d.deviceId}>
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
                      {d.name}
                      <span className="text-muted-foreground text-xs ml-1">
                        {d.model}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {offline.length > 0 && (
              <SelectGroup>
                <SelectLabel>Offline</SelectLabel>
                {offline.map((d) => (
                  <SelectItem key={d.deviceId} value={d.deviceId}>
                    <span className="flex items-center gap-2 opacity-50">
                      <span className="h-2 w-2 rounded-full bg-gray-400 shrink-0" />
                      {d.name}
                      <span className="text-muted-foreground text-xs ml-1">
                        {d.model}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            )}
            {devices.length === 0 && !isLoading && (
              <SelectItem value="__empty__" disabled>
                No devices discovered
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {currentBinding && (
        <div className="text-xs text-muted-foreground">
          Bound to: <strong>{currentBinding.deviceName}</strong>
          {currentBinding.capabilities.brightness ? " (dimmer)" : " (switch)"}
        </div>
      )}
    </div>
  );
};
